import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box } from "@mui/material";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import YetiKpiBox from "../../components/YetiKpiBox";
import MetricTargetsDialog from "./MetricTargetsDialog";
import { UseIsAdmin, UseUserId } from "../../hooks/auth";

/**
 * The "Team Performance vs. The Yeti" / "My Performance vs. The Yeti" card —
 * extracted from Homepage so it can live as a block on the customizable
 * dashboard. Card markup, the header gear / target dialog, and the KPI list
 * are unchanged from the original inline version; only the wiring (own hooks
 * + dialog state) moved in here.
 *
 * Admins keep the workspace-wide average (`kpis` as handed down by
 * ScopedCard). A non-admin player is one specific person, not a team, so
 * their card swaps each KPI's amount/points for their own row out of
 * `people` (the same per-player data ScopedCard's own byPlayer picker uses) —
 * same shape as ScopedCard's byPlayer override, just always aimed at "me".
 */
const TeamYetiCard = ({ kpis = [], title, periodIdx = 3, yetiStats = null, people = [] }) => {
  const isAdmin = UseIsAdmin();
  const userId = UseUserId();
  const navigate = useNavigate();

  const displayKpis = useMemo(() => {
    if (isAdmin) return kpis;
    const me = (people || []).find((p) => String(p.userId) === String(userId));
    if (!me) return kpis;
    return kpis.map((k) => ({
      ...k,
      amount: Number(me.kpis?.[String(k._id)]?.amt) || 0,
      points: Number(me.kpis?.[String(k._id)]?.pts) || 0,
    }));
  }, [kpis, isAdmin, userId, people]);

  const defaultTitle = isAdmin ? "Team Performance vs. The Yeti" : "My Performance vs. The Yeti";
  // Must match DashboardCanvas's own stack breakpoint ("lg") — below that the
  // dashboard renders cards in an auto-height stack, not a fixed-height grid
  // cell. A narrower threshold here (e.g. "md") left this card applying its
  // grid-cell height/maxHeight in the 768-1023px tablet band while already
  // stacked, and its content could overflow that cap into the card below.
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const screenWidth = window.innerWidth;
  const [openDailyTargetDialog, setOpenDailyTargetDialog] = useState(false);

  return (
    <SoftBox
      sx={{
        height: { mini: "835px", xxs: "875px", md: "100%" },
        // Page gutters: Homepage SoftBox marginX. Flush card to the stack edge.
        paddingLeft: 0,
        paddingRight: 0,
        ...(isSmallScreen && ({ height: "unset", minHeight: "100px !important" })),
      }}
    >
      <SoftBox
        sx={{
          background: "var(--tvd-card, #fff)",
          paddingTop: 0,
          paddingBottom: "2rem",
          // Clip both axes (not just X) — like every other dashboard card's
          // outer box (see LeaderBoard's `overflow: "clip"`) — so content can
          // never visually spill past this card's rounded edge into whatever
          // is stacked below it.
          overflow: "hidden",
          borderRadius: "8px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          maxHeight: { mini: "960px", xxs: "1000px", md: "850px" },
          ...(isSmallScreen && ({ maxHeight: "unset", minHeight: "100px" })),
        }}
      >
        <SoftBox
          sx={{
            height: "56px",
            paddingLeft: { mini: "16px", sm: "20px" },
            paddingRight: { mini: "16px", sm: "20px" },
            borderBottom: "1px solid var(--tvd-hair, #EAECF0)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <SoftTypography
            sx={{
              fontFamily: "DM Sans",
              textFillColor: "var(--tvd-ink, #0F172A)",
              color: "var(--tvd-ink, #0F172A)",
              fontSize: "16px",
              lineHeight: 1.2,
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            {title || defaultTitle}
          </SoftTypography>
          <SoftTypography
            onClick={() => (isAdmin ? navigate("/workspace-kpi") : setOpenDailyTargetDialog(true))}
            sx={{
              color: "primary.main",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              // Larger tap target on mobile only — the icon alone was ~20-25px,
              // under the ~32-36px comfortable-touch guideline. Desktop is untouched.
              // No offsetting negative margin here: as the last flex child in this
              // space-between header, padding alone grows the tap target inward
              // without pushing it past the header's padding into the card's
              // clipped (overflow: hidden) rounded corner.
              ...(isSmallScreen && ({ padding: "8px" })),
            }}
          >
            {isAdmin ? (
              <svg width={screenWidth === 1024 ? "20" : "25"} height={screenWidth === 1024 ? "20" : "24"} viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 13.125C8.775 13.125 7.375 11.725 7.375 10C7.375 8.275 8.775 6.875 10.5 6.875C12.225 6.875 13.625 8.275 13.625 10C13.625 11.725 12.225 13.125 10.5 13.125ZM10.5 8.125C9.46667 8.125 8.625 8.96667 8.625 10C8.625 11.0333 9.46667 11.875 10.5 11.875C11.5333 11.875 12.375 11.0333 12.375 10C12.375 8.96667 11.5333 8.125 10.5 8.125Z" fill="var(--tvd-ink, #292D32)"/>
                <path d="M13.1748 18.4932C12.9998 18.4932 12.8248 18.4682 12.6498 18.4265C12.1332 18.2849 11.6998 17.9599 11.4248 17.5015L11.3248 17.3349C10.8332 16.4849 10.1582 16.4849 9.6665 17.3349L9.57484 17.4932C9.29984 17.9599 8.8665 18.2932 8.34984 18.4265C7.82484 18.5682 7.28317 18.4932 6.82484 18.2182L5.3915 17.3932C4.88317 17.1015 4.5165 16.6265 4.35817 16.0515C4.20817 15.4765 4.28317 14.8849 4.57484 14.3765C4.8165 13.9515 4.88317 13.5682 4.7415 13.3265C4.59984 13.0849 4.2415 12.9432 3.74984 12.9432C2.53317 12.9432 1.5415 11.9515 1.5415 10.7349V9.26821C1.5415 8.05154 2.53317 7.05987 3.74984 7.05987C4.2415 7.05987 4.59984 6.91821 4.7415 6.67654C4.88317 6.43487 4.82484 6.05154 4.57484 5.62654C4.28317 5.11821 4.20817 4.51821 4.35817 3.95154C4.50817 3.37654 4.87484 2.90154 5.3915 2.60987L6.83317 1.78487C7.77484 1.22654 9.0165 1.55154 9.58317 2.50987L9.68317 2.67654C10.1748 3.52654 10.8498 3.52654 11.3415 2.67654L11.4332 2.51821C11.9998 1.55154 13.2415 1.22654 14.1915 1.79321L15.6248 2.61821C16.1332 2.90987 16.4998 3.38487 16.6582 3.95987C16.8082 4.53487 16.7332 5.12654 16.4415 5.63487C16.1998 6.05987 16.1332 6.44321 16.2748 6.68488C16.4165 6.92654 16.7748 7.06821 17.2665 7.06821C18.4832 7.06821 19.4748 8.05987 19.4748 9.27654V10.7432C19.4748 11.9599 18.4832 12.9515 17.2665 12.9515C16.7748 12.9515 16.4165 13.0932 16.2748 13.3349C16.1332 13.5765 16.1915 13.9599 16.4415 14.3849C16.7332 14.8932 16.8165 15.4932 16.6582 16.0599C16.5082 16.6349 16.1415 17.1099 15.6248 17.4015L14.1832 18.2265C13.8665 18.4015 13.5248 18.4932 13.1748 18.4932ZM10.4998 15.4099C11.2415 15.4099 11.9332 15.8765 12.4082 16.7015L12.4998 16.8599C12.5998 17.0349 12.7665 17.1599 12.9665 17.2099C13.1665 17.2599 13.3665 17.2349 13.5332 17.1349L14.9748 16.3015C15.1915 16.1765 15.3582 15.9682 15.4248 15.7182C15.4915 15.4682 15.4582 15.2099 15.3332 14.9932C14.8582 14.1765 14.7998 13.3349 15.1665 12.6932C15.5332 12.0515 16.2915 11.6849 17.2415 11.6849C17.7748 11.6849 18.1998 11.2599 18.1998 10.7265V9.25987C18.1998 8.73487 17.7748 8.30154 17.2415 8.30154C16.2915 8.30154 15.5332 7.93487 15.1665 7.29321C14.7998 6.65154 14.8582 5.80987 15.3332 4.99321C15.4582 4.77654 15.4915 4.51821 15.4248 4.26821C15.3582 4.01821 15.1998 3.81821 14.9832 3.68487L13.5415 2.85987C13.1832 2.64321 12.7082 2.76821 12.4915 3.13487L12.3998 3.29321C11.9248 4.11821 11.2332 4.58487 10.4915 4.58487C9.74984 4.58487 9.05817 4.11821 8.58317 3.29321L8.4915 3.12654C8.28317 2.77654 7.8165 2.65154 7.45817 2.85987L6.0165 3.69321C5.79984 3.81821 5.63317 4.02654 5.5665 4.27654C5.49984 4.52654 5.53317 4.78487 5.65817 5.00154C6.13317 5.81821 6.1915 6.65987 5.82484 7.30154C5.45817 7.94321 4.69984 8.30987 3.74984 8.30987C3.2165 8.30987 2.7915 8.73487 2.7915 9.26821V10.7349C2.7915 11.2599 3.2165 11.6932 3.74984 11.6932C4.69984 11.6932 5.45817 12.0599 5.82484 12.7015C6.1915 13.3432 6.13317 14.1849 5.65817 15.0015C5.53317 15.2182 5.49984 15.4765 5.5665 15.7265C5.63317 15.9765 5.7915 16.1765 6.00817 16.3099L7.44984 17.1349C7.62484 17.2432 7.83317 17.2682 8.02484 17.2182C8.22484 17.1682 8.3915 17.0349 8.49984 16.8599L8.5915 16.7015C9.0665 15.8849 9.75817 15.4099 10.4998 15.4099Z" fill="var(--tvd-ink, #292D32)"/>
              </svg>
            ) : (
              <svg width={screenWidth === 1024 ? "20" : "24"} height={screenWidth === 1024 ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_12719_54496)">
                <path d="M19.257 10.7383C19.116 12.5104 18.4692 14.2049 17.3935 15.6202C16.3179 17.0356 14.8585 18.1125 13.1888 18.7229C11.5191 19.3333 9.70932 19.4515 7.9744 19.0636C6.23949 18.6757 4.65239 17.7979 3.40166 16.5346C2.15093 15.2712 1.28914 13.6754 0.918692 11.9366C0.548239 10.1979 0.684688 8.3894 1.31183 6.72593C1.93897 5.06247 3.03044 3.614 4.45657 2.5526C5.88269 1.4912 7.58353 0.861489 9.35701 0.738281" stroke="var(--tvd-body, #475467)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.9856 11.5577C13.7276 12.2217 13.3082 12.8109 12.7653 13.2721C12.2223 13.7332 11.573 14.0518 10.876 14.1989C10.1791 14.3461 9.45639 14.3172 8.77339 14.1148C8.09038 13.9125 7.46859 13.543 6.96424 13.04C6.45988 12.5369 6.08888 11.9161 5.88477 11.2336C5.68066 10.5511 5.64989 9.82854 5.79524 9.13118C5.94059 8.43382 6.25747 7.78369 6.71723 7.23957C7.17699 6.69546 7.76512 6.27453 8.42845 6.01484M9.99988 10.0006L13.5713 6.42913M13.5713 6.42913L16.4285 7.14341L19.2856 4.28627L16.4285 3.57199L15.7142 0.714844L12.857 3.57199L13.5713 6.42913Z" stroke="var(--tvd-body, #475467)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_12719_54496">
                <rect width="24" height="24" fill="white"/>
                </clipPath>
                </defs>
              </svg>
            )}
          </SoftTypography>
          <MetricTargetsDialog
            open={openDailyTargetDialog}
            onClose={() => setOpenDailyTargetDialog(false)}
            kpis={displayKpis}
          />
        </SoftBox>
        <Box
          sx={{
            marginX: "auto",
            width: "100%",
            paddingX: { mini: "0.5rem", md: "1rem", lg: "1rem", xl: "1.25rem" },
            paddingTop: { mini: "0.75rem", md: "0.75rem" },
            paddingBottom: { mini: "1rem", md: "1.25rem" },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minHeight: 0,
          }}
        >
          <SoftBox sx={{ padding: { mini: "0 16px 0 16px", lg: "unset" }, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <YetiKpiBox kpis={displayKpis} periodIdx={periodIdx} yetiStats={yetiStats} />
          </SoftBox>
        </Box>
      </SoftBox>
    </SoftBox>
  );
};

export default TeamYetiCard;
